import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Calendar,
} from 'lucide-react';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { joinFullName } from '../../utils/patientName';
import {
  usePatients,
  useDepartments,
  useDoctors,
  useCreateVisit,
} from '../../services/httpServices/queries';

export function VisitForm() {
  const navigate = useNavigate();
  const { data: patientsData, isLoading: patientsLoading } = usePatients(1, 100, {
    isActive: true,
  });
  const { data: departmentsData, isLoading: departmentsLoading } = useDepartments();
  const { data: doctorsData, isLoading: doctorsLoading } = useDoctors(1, 100);
  const createVisit = useCreateVisit();

  const patients = patientsData?.data || [];
  const departments = departmentsData?.data || [];
  const doctors = doctorsData?.data || [];

  const patientOptions = patients.map((p: any) => ({
    value: p.id,
    label: joinFullName(p) || p.registrationNumber || p.mobileNumber || p.id,
  }));

  const departmentOptions = departments.map((d: any) => ({
    value: d.id,
    label: d.name || d.id,
  }));

  const doctorOptions = doctors.map((d: any) => ({
    value: d.id,
    label: d.name || d.employeeCode || d.id,
  }));

  const [formData, setFormData] = useState<{
    patientId: string;
    type: 'OPD' | 'IPD' | 'ER' | 'EXT';
    departmentId: string;
    referredById: string;
  }>({
    patientId: '',
    type: 'OPD',
    departmentId: '',
    referredById: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createVisit.mutate(formData, {
      onSuccess: () => {
        navigate('/visits');
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/visits')}
          className="p-2 hover:bg-background rounded-lg transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-5 h-5 text-text" />
        </button>
        <div className="flex items-center gap-3">
          <Calendar className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">New Visit</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-surface rounded-xl border border-border p-6 space-y-6 max-w-2xl">
        <SearchableSelect
          label="Patient *"
          labelClassName="block text-sm font-medium text-text mb-1"
          value={formData.patientId}
          onChange={(value) => setFormData({ ...formData, patientId: value })}
          options={patientOptions}
          placeholder="Select patient..."
          loading={patientsLoading}
        />

        <div>
          <label className="block text-sm font-medium text-text mb-1">Visit Type *</label>
          <select
            required
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as 'OPD' | 'IPD' | 'ER' | 'EXT' })}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="OPD">OPD</option>
            <option value="IPD">IPD</option>
            <option value="ER">Emergency</option>
            <option value="EXT">External</option>
          </select>
        </div>

        <SearchableSelect
          label="Department"
          labelClassName="block text-sm font-medium text-text mb-1"
          value={formData.departmentId}
          onChange={(value) => setFormData({ ...formData, departmentId: value })}
          options={departmentOptions}
          placeholder="Select department..."
          loading={departmentsLoading}
        />

        <SearchableSelect
          label="Referred By (Doctor)"
          labelClassName="block text-sm font-medium text-text mb-1"
          value={formData.referredById}
          onChange={(value) => setFormData({ ...formData, referredById: value })}
          options={doctorOptions}
          placeholder="Select referring doctor..."
          loading={doctorsLoading}
        />

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => navigate('/visits')}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createVisit.isPending || !formData.patientId}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {createVisit.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            Create Visit
          </button>
        </div>
      </form>
    </div>
  );
}
